import { useEffect, useState } from 'react';
import { onSnapshot } from 'firebase/firestore';
import { projectDoc } from '../constants/firestorePaths';

/**
 * Live read of a project's optional `client` field (sponsor / client
 * organization name).
 *
 * An administrative label only. It formerly pooled reusable CRF completion
 * instructions across projects sharing the name; that serving path is closed
 * (`CROSS_PROJECT_SERVING_ENABLED`), so matching spellings now grant nothing.
 * `client` is `null` while loading and whenever the field is absent or blank.
 */
export function useProjectClient(projectId: string | undefined): {
  client: string | null;
  loading: boolean;
} {
  const [client, setClient] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) {
      setClient(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = onSnapshot(
      projectDoc(projectId),
      snap => {
        const data = snap.data() as { client?: string | null } | undefined;
        const value =
          typeof data?.client === 'string' ? data.client.trim() : '';
        setClient(value || null);
        setLoading(false);
      },
      err => {
        console.warn('useProjectClient snapshot error:', err);
        setClient(null);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [projectId]);

  return { client, loading };
}
